import { AxiosRequestConfig } from 'axios'
import { useApi } from '../hooks'
import {
  Aurora,
  AuroraAwhpData,
  AuroraAwhpDataQuery,
  AuroraAwhpEvent,
  AuroraAwhpEventQuery,
  AuroraAwhpStatus,
  AuroraInput
} from '../types'
import { subMinutes } from 'date-fns'
import AuroraAwhpActionInput from '../types/AuroraAwhpActionInput'
import {
  AuroraCirculationPumpMode,
  AuroraCirculationPumpStatus,
  AuroraHeatPumpStatus
} from '../enums'
import { ulid } from 'ulid'
import { AuroraAwhpActionNack } from '../errors'

const api = useApi('v2')

export async function getAuroras(config?: AxiosRequestConfig) {
  const response = await api.get<Aurora[]>('/auroras', config)

  return response.data
}

export async function getAurora(id: string, config?: AxiosRequestConfig) {
  const response = await api.get<Aurora>(`/auroras/${id}`, config)

  return response.data
}

export async function updateAurora(id: string, input: AuroraInput, config?: AxiosRequestConfig) {
  const response = await api.put<Aurora>(`/auroras/${id}`, input, config)

  return response.data
}

export async function getAuroraAwhpData(
  query: AuroraAwhpDataQuery,
  config?: Omit<AxiosRequestConfig, 'params'>
) {
  const response = await api.get<AuroraAwhpData[]>(`/auroras/awhp-data`, {
    params: query,
    ...config
  })

  return response.data
}

export async function getAuroraAwhpDataNow(
  id: string,
  config?: Omit<AxiosRequestConfig, 'params'>
) {
  const response = await api.get<AuroraAwhpData[]>(`/auroras/${id}/awhp-data`, {
    ...config,
    params: {
      aurora_id: id,
      from: subMinutes(new Date(), 3),
      to: new Date()
    }
  })

  if (response.data.length === 0) {
    throw new Error(`No AWHP data found for Aurora ${id}`)
  }

  return response.data[0]
}

export async function getAuroraAwhpEvents(
  query: AuroraAwhpEventQuery,
  config?: Omit<AxiosRequestConfig, 'params'>
) {
  const response = await api.get<AuroraAwhpEvent[]>(`/auroras/awhp-events`, {
    params: query,
    ...config
  })

  return response.data
}

export async function publishAuroraAwhpAction(
  id: string,
  action: AuroraAwhpActionInput,
  config?: AxiosRequestConfig
) {
  const correlationId = ulid()

  await api.post(
    `/auroras/${id}/awhp/action`,
    {
      ...action,
      correlationId
    },
    config
  )

  // Wait for one second before polling for status.
  const ONE_SECOND = 1000
  await wait(ONE_SECOND)

  const status = await pollAuroraAwhpStatus(id, correlationId, config)

  if (status?.action_nack) {
    throw new AuroraAwhpActionNack(status.action_nack)
  }
}

export async function setAuroraHeatPumpStatus(
  id: string,
  value: AuroraHeatPumpStatus,
  config?: AxiosRequestConfig
) {
  await publishAuroraAwhpAction(
    id,
    {
      hp: {
        system_on: value
      }
    },
    config
  )
}

export async function setAuroraTargetTemperature(
  id: string,
  value: number,
  config?: AxiosRequestConfig
) {
  await publishAuroraAwhpAction(
    id,
    {
      hp: {
        setTarget1: value
      }
    },
    config
  )
}

export async function setAuroraCirculationPumpStatus(
  id: string,
  value: AuroraCirculationPumpStatus,
  config?: AxiosRequestConfig
) {
  await publishAuroraAwhpAction(
    id,
    {
      pump: {
        run: value
      }
    },
    config
  )
}

export async function setAuroraCirculationPumpMode(
  id: string,
  mode: AuroraCirculationPumpMode,
  config?: AxiosRequestConfig
) {
  await publishAuroraAwhpAction(
    id,
    {
      pump: {
        mode
      }
    },
    config
  )
}

export async function getAuroraAwhpActiveEvents(id: string, config?: AxiosRequestConfig) {
  const response = await api.get<AuroraAwhpEvent[]>(`/auroras/${id}/awhp/active-events`, config)

  return response.data
}

export async function getAuroraAwhpStatus(
  id: string,
  correlationId: string,
  config?: AxiosRequestConfig
) {
  const response = await api.get<AuroraAwhpStatus>(`/auroras/${id}/awhp/status/${correlationId}`, {
    ...config
  })

  return response.data
}

export async function pollAuroraAwhpStatus(
  id: string,
  correlationId: string,
  config?: AxiosRequestConfig
) {
  const startTime = Date.now()

  const THREE_SECONDS = 3 * 1000
  const THIRTY_SECONDS = 30 * 1000

  while (Date.now() - startTime < THIRTY_SECONDS) {
    try {
      const status = await getAuroraAwhpStatus(id, correlationId, config)

      return status
    } catch {
      await wait(THREE_SECONDS)
    }
  }

  throw new Error('Timeout while polling for Aurora AWHP status')
}

async function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}
